<template>
  <div class="w-full bg-white dark:bg-gray-800/50 dark:border-gray-800 rounded-xl border p-3 mb-2">
    <div class="w-full flex flex-wrap md:flex-nowrap space-y-3 md:space-y-0 space-x-3 items-center">
      <div class="block self-stretch relative pr-1">
        <div
          class="w-1 rounded h-full"
          :style="{
            backgroundColor: colour
          }"
        />
      </div>

      <div class="pt-1">
        <p class="text-sm opacity-80">
          {{ startLabel }} - {{ finishLabel }}
        </p>
        <p class="text-base font-semibold leading-tight md:leading-loose my-1 md:my-0">
          {{ _service.service.title }}
        </p>
        <div class="text-sm opacity-80 flex flex-wrap leading-none">
          <span class="flex items-center mr-4 mb-1 leading-tight">
            <o-avatar :items="[ _service.employee ]" class="hidden md:flex pr-2" />
            <span>{{ _service.employee.displayName }}</span>
          </span>
          <div class="flex items-center">
            <span class="flex items-center mr-4 mb-1 leading-tight">
              <o-icon class="hidden md:block mr-2" icon="menuBookings" />
              <span>{{ durationLabel }}</span>
            </span>
            <span class="flex items-center mr-4 mb-1 leading-tight">
              <o-icon class="hidden md:block mr-2" icon="price" />
              <service-price :service="_service" />
            </span>
            <span v-if="service.association" class="flex items-center mr-4 mb-1 leading-tight">
              <o-icon class="hidden md:block mr-2" icon="charge" />
              <span>{{ $t('service.addOn') }}</span>
            </span>
          </div>
        </div>

        <div v-if="_service.resources" class="flex items-center flex-wrap ml-1 opacity-80 text-sm">
          <template v-for="reservationResource in _service.resources" :key="reservationResource.idReservationResource">
            <span v-if="reservationResource.resource" class="flex items-center mr-2 mb-1 leading-tight bg-gray-50 dark:bg-gray-900 p-1 rounded">
              <o-icon class="hidden md:block mr-2 text-sm" :size="13" icon="resources" /><span v-html="$t('components.reservation.elements.service.name', { name: reservationResource.serviceResource.name, displayName: reservationResource.resource.displayName })" />
            </span>
          </template>
        </div>
      </div>

      <div class="flex items-center space-x-2 justify-end flex-grow">
        <o-tooltip v-if="allowEdit" :text="$t('service.edit')">
          <o-button icon="edit" flat size="sm" @click="editService" />
        </o-tooltip>
        <o-tooltip v-if="allowRemove" :text="$t('components.reservation.elements.service.remove')">
          <o-button
            icon="delete"
            flat
            variant="danger"
            size="sm"
            @click="removeService"
          />
        </o-tooltip>



        <o-history-link
          v-if="(isAdmin || isDev) && hasPersistedService"
          :item="service"
          icon="history"
          size="sm"
          slim
          flat
          model="ReservationService"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ServicePrice from './ServicePrice.vue'
import {
  OAvatar,
} from '@/components/elements'

const { $auth, $currency, $config } = useNuxtApp()

interface Props {
  service: Record<string, any>
  serviceType?: string | null
  allowRemove?: boolean
  allowEdit?: boolean
}

interface Emits {
  (e: 'update:service', value: any): void
  (e: 'edit'): void
  (e: 'remove'): void
}

const props = withDefaults(defineProps<Props>(), {
  serviceType: null,
  allowRemove: false,
  allowEdit: false,
})

const emit = defineEmits<Emits>()

const _service = computed({
  get () {
    return props.service
  },
  set (value) {
    emit('update:service', value)
  },
})

const colour = computed(() => {
  const service = props.service.service
  const c = service ? service.borderHexColour : 'bbbbbb'

  return '#' + c
})

const voucher = computed(() => props.service.voucher)
const customerPass = computed(() => props.service.customerPass)
const discount = computed(() => props.service.voucher || props.service.customerPass)
const priceLabel = computed(() => $auth.info.location.currencySymbol + props.service.price)
const startLabel = computed(() => props.service.startLabel)
const finishLabel = computed(() => props.service.finishLabel)
const durationLabel = computed(() => props.service.durationLabel)

const discountLabel = computed(() => {
  return $currency.format(props.service.price - (props.service.discountTotal || 0))
})

const isDev = computed(() => $config.public.instance === 'dev')
const isAdmin = computed(() => $auth?.info?.adminUser)
const hasPersistedService = computed(() => Number(props.service.idReservationService) > 0)

function editService () {
  emit('edit')
}

function removeService () {
  emit('remove')
}
</script>
